import styled, { css } from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const PageHeader = styled.div`
  width: 100%;
  padding-bottom: 32px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

export const PageTitle = styled.h1`
  align-self: stretch;
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 700;
  line-height: 1.358;
  margin: 0;
`;

export const PageContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

export const LoadingState = styled.div`
  width: 100%;
  text-align: center;
  padding: 2rem;
`;

/* CSS 변수 */
const cssVars = css`
  --Label-Normal: #171719;
  --Label-Neutral: rgba(46, 47, 51, 0.88);
  --Label-Alternative: rgba(55, 56, 60, 0.61);
  --Label-Assistive: rgba(55, 56, 60, 0.28);
  --Primary-Normal: #448181;
  --Line-Normal-Neutral: rgba(112, 115, 124, 0.16);
  --Static-White: white;
`;

/* Vimeo 섹션 */
export const VimeoSection = styled.div`
  ${cssVars}
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 8px;
`;

/* 입력 그룹 */
export const InputGroup = styled.div`
  ${cssVars}
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const Label = styled.div`
  ${cssVars}
  color: var(--Label-Neutral);
  font-size: 14px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

export const InputWrapper = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

export const Input = styled.input`
  ${cssVars}
  flex: 1 1 0;
  padding: 12px;
  position: relative;
  border-radius: 12px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  display: flex;
  border: 1px var(--Line-Normal-Neutral) solid;
  background: rgba(0, 0, 0, 0);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: var(--Label-Normal);
  height: 48px;

  &::placeholder {
    color: var(--Label-Assistive);
  }

  &:focus {
    outline: none;
    border-color: var(--Primary-Normal);
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
    border-color: #ddd;
  }
`;

export const Select = styled.select`
  ${cssVars}
  flex: 1 1 0;
  padding: 12px;
  position: relative;
  border-radius: 12px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  display: flex;
  border: 1px var(--Line-Normal-Neutral) solid;
  background: rgba(0, 0, 0, 0);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: var(--Label-Normal);
  height: 48px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--Primary-Normal);
  }

  option {
    color: var(--Label-Assistive);
  }
`;

/* 동기화 버튼 */
export const SyncButton = styled.button`
  ${cssVars}
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  outline: 1px var(--Line-Normal-Neutral) solid;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 100px;
  height: 48px;

  &:hover {
    background: var(--Label-Normal);
    opacity: 0.1;
  }
`;

export const SyncButtonText = styled.div`
  ${cssVars}
  white-space: nowrap;
  color: var(--Primary-Normal);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

/* 이미지 업로드 */
export const ImageUpload = styled.div<{ $disabled?: boolean }>`
  ${cssVars}
  cursor: pointer;
  border-radius: 12px;
  border: 1px var(--Line-Normal-Neutral) solid;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  &:hover {
    border-color: var(--Primary-Normal);
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

export const ImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
`;

export const UploadedImage = styled.img`
  width: 100%;
  aspect-ratio: 3/1;
  object-fit: cover;
  border-radius: 12px;
`;

export const UploadIcon = styled.div`
  ${cssVars}
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: var(--Primary-Normal);
`;

/* 이미지 섹션 */
export const ImageSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ImageGroup = styled.div`
  flex: 1 1 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const ThumbnailUpload = styled(ImageUpload)`
  width: 271px;
  align-self: stretch;
  aspect-ratio: 3/2;
  min-height: 180.66px;
`;

export const BannerUpload = styled(ImageUpload)`
  flex: 1 1 0;
  width: 100%;
  min-height: 180.66px;
`;

/* 이미지 삭제 버튼 (X 버튼) */
export const ImageDeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

export const DeleteIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  color: white;
  font-weight: 300;
`;

/* 강연자 선택 섹션 */
export const LecturerSelectionSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const LecturerRadioRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

/* 강연자 기본 섹션 */
export const LecturerSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* 강의 자료 섹션 */
export const MaterialsSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
`;

export const MaterialsWrapper = styled.div`
  width: 100%;
  flex: 1 1 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

export const MaterialsDescription = styled.div`
  ${cssVars}
  align-self: stretch;
  color: var(--Label-Alternative);
  font-size: 13px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
  display: flex;
  align-items: center;
`;

export const FileUploadButton = styled.label<{ $disabled?: boolean }>`
  ${cssVars}
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  outline: 1px var(--Line-Normal-Neutral) solid;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 130px;
  height: 48px;

  &:hover {
    background: var(--Label-Normal);
    opacity: 0.1;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

export const FileUploadButtonText = styled.div`
  ${cssVars}
  color: var(--Primary-Normal);
  white-space: nowrap;
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

export const FileList = styled.div`
  ${cssVars}
  margin-top: 16px;
  border: 1px solid var(--Line-Normal-Neutral);
  border-radius: 12px;
  padding: 16px;
  background-color: var(--Static-White);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export const FileListTitle = styled.div`
  ${cssVars}
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--Label-Normal);
  font-size: 14px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
`;

export const FileItem = styled.div`
  ${cssVars}
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 12px 8px;
  margin-bottom: 8px;
  border: 1px solid var(--Line-Normal-Neutral);
  border-radius: 8px;
  background-color: #fafbfc;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: #f5f6f7;
    border-color: var(--Primary-Normal);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.div`
  ${cssVars}
  font-weight: 500;
  color: var(--Label-Normal);
  margin-bottom: 4px;
  font-size: 14px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  word-break: break-all;
`;

export const FileUrl = styled.div`
  margin-top: 4px;
`;

export const FileLink = styled.a`
  ${cssVars}
  color: var(--Primary-Normal);
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #2d5a5a;
    text-decoration: underline;
  }
`;

export const RemoveFileButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 300;
  padding: 0;
  border-radius: 50%;
  transition: all 0.2s ease;
  min-width: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

/* 이미지 업로드 섹션들 */
export const ImageUploadSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ImageUploadGroup = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const ImageUploadWrapper = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
`;

export const ImageUploadDescription = styled.div`
  ${cssVars}
  align-self: stretch;
  color: var(--Label-Alternative);
  font-size: 13px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

export const ImagePreview = styled.div`
  ${cssVars}
  position: relative;
  border: 1px var(--Line-Normal-Neutral) solid;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  background: #f8f9fa;
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: contain;
  background: white;
`;

export const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 300;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

export const UploadingText = styled.div`
  ${cssVars}
  margin-top: 8px;
  font-size: 12px;
  color: var(--Primary-Normal);
  text-align: center;
  font-weight: 500;
`;

/* 추천 강의 섹션 */
export const RecommendedSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 8px;
`;

export const RecommendedImageSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
`;

export const RecommendedImageGroup = styled.div`
  width: 263px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  aspect-ratio: 3/2;
  min-height: 175.33px;
`;

export const RecommendedImageUpload = styled.div`
  ${cssVars}
  position: relative;
  border-radius: 12px;
  border: 1px var(--Line-Normal-Neutral) solid;
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--Primary-Normal);
  }
`;

export const RecommendedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

export const RecommendedImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const RecommendedPlusContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const RecommendedRemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 300;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

export const RecommendedImageTitle = styled.span`
  font-size: 0.9rem;
  color: #333;
  text-align: center;
  word-break: break-word;
`;

/* FAQ 섹션 */
export const FaqSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
`;

export const FaqList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FaqItem = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

export const FaqInputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FaqQuestionWrapper = styled.div`
  width: 100%;
`;

export const FaqAnswerWrapper = styled.div`
  width: 100%;
`;

export const FaqButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

export const FaqInput = styled.input`
  ${cssVars}
  width: 100%;
  padding: 12px;
  position: relative;
  border-radius: 12px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  display: flex;
  border: 1px var(--Line-Normal-Neutral) solid;
  background: rgba(0, 0, 0, 0);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: var(--Label-Normal);
  height: 48px;

  &::placeholder {
    color: var(--Label-Assistive);
  }

  &:focus {
    outline: none;
    border-color: var(--Primary-Normal);
  }
`;

export const FaqAddButton = styled.button`
  ${cssVars}
  padding: 12px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  outline: 1px var(--Line-Normal-Neutral) solid;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 48px;
  height: 48px;
  font-size: 20px;
  font-weight: 400;
  color: var(--Primary-Normal);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(68, 129, 129, 0.1);
  }
`;

export const FaqRemoveButton = styled.button`
  ${cssVars}
  padding: 12px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  outline: 1px var(--Line-Normal-Neutral) solid;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  width: 48px;
  height: 48px;
  font-size: 20px;
  font-weight: 400;
  transition: all 0.2s ease;
  color: white;
  background: rgba(0, 0, 0, 0.5);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:disabled:hover {
    background: transparent;
  }
`;

/* 모달 */
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 95%;
    margin: 1rem;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
`;

export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.5rem;
  border-radius: 4px;

  &:hover {
    background-color: #f8f9fa;
    color: #333;
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

export const LectureListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const LectureListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #448181;
    background-color: #f8f9fa;
  }
`;

export const LectureCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

export const LectureItemImageContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const LectureItemThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const LectureItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const LectureItemTitle = styled.span`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const LectureItemTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

export const Tag = styled.span`
  background-color: #e9ecef;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
`;

export const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
`;

export const ConfirmButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #448181;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #3a6b6b;
  }
`;

/* 버튼 섹션 */
export const ButtonSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const TempSaveWrapper = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;
`;

export const TempSaveButton = styled.button`
  ${cssVars}
  padding-top: 4px;
  padding-bottom: 4px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 74px;
  height: 32px;

  &:hover:not(:disabled) {
    opacity: 0.5;
    border-radius: 6px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const TempSaveButtonText = styled.div`
  ${cssVars}
  text-align: center;
  color: var(--Label-Alternative);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

export const SaveWrapper = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

export const SaveButton = styled.button`
  ${cssVars}
  align-self: stretch;
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  background: var(--Primary-Normal);
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  width: 160px;
  height: 48px;

  &:hover:not(:disabled) {
    background: var(--Label-Normal);
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SaveButtonText = styled.div`
  ${cssVars}
  color: var(--Static-White);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

/* 강의 삭제 버튼 */
export const DeleteWrapper = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

export const DeleteButton = styled.button`
  align-self: stretch;
  padding-left: 28px;
  padding-right: 28px;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  background: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  width: 160px;
  height: 48px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const DeleteButtonText = styled.div`
  ${cssVars}
  color: var(--Static-White);
  font-size: 16px;
  font-family: 'Pretendard Variable', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;
